import { useId, useMemo } from "react";
import ProductCategoryDistributionFormItem from "./ProductCategoryDistributionFormItem";
import { formatCurrency, formatDecimalUnit } from "@/common/utils/formatters";
import Select from "@/common/components/Select";
import { useFieldArray, useFormContext } from "react-hook-form";
import { unFormatCurrency, unFormatUnknownUnit } from "@/common/utils/unFormatters";
import { CreateGovernmentAllocationDistributionQuantityValidationSchema, CreateGovernmentAllocationDistributionValidationSchema } from '../../schemas/createGovernmentAllocationDistribution.schema';
import { CompanyGovernmentAllocationDistribution, CompanyGovernmentAllocationProductCategoriesDistribution } from '../../types/governmentAllocationDistribution.type';
import { Option } from '@/common/types/option';
import { GovernmentAllocationAllowedCategoriesAndProducts, GovernmentAllocationAllowedCategoryAndProductData } from '../../types/governmentAllocation.type';
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from '../../hooks/useGovernmentAllocationsAllowedCategoriesAndProducts';
import { useParams } from 'react-router-dom';
import { GovernmentAllocationsAllowedCategoriesAndProductsTransformer } from '../../services/governmentAllocationAllowedCategoriesAndProductsTransformer';

interface GovernmentAllocationDistributionProductCategoriesFormProps {
  distribution: CompanyGovernmentAllocationDistribution;
  isConsumptionControlledByQuantity?: boolean;
}

const GovernmentAllocationDistributionProductCategoriesForm = ({
  distribution,
  isConsumptionControlledByQuantity,
}: GovernmentAllocationDistributionProductCategoriesFormProps) => {
  const uniqueId = useId();

  const { id } = useParams();

  const { control, setValue, watch } = useFormContext<
    | CreateGovernmentAllocationDistributionValidationSchema
    | CreateGovernmentAllocationDistributionQuantityValidationSchema
  >();

  const {
    data: allowedCategoriesAndProducts,
    isFetching: isAllowedCategoriesAndProductsFetching,
    isPending: isAllowedCategoriesAndProductsPending
  } = useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
    governmentAllocationId: id,
    filters: {},
  });

  const allowedCategoriesOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending
      ? []
      : GovernmentAllocationsAllowedCategoriesAndProductsTransformer.optionsTransformer('categories', isConsumptionControlledByQuantity)(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending]);

  const {
    fields: productCategoriesDistributions,
    append: appendProductCategoryDistribution,
    remove: removeProductCategoryDistribution,
  } = useFieldArray({
    control,
    name: 'product_categories_distributions',
  });

  const setTotalValue = () => {
    if (isConsumptionControlledByQuantity) {
      const productCategoriesTotalQuantitySum = productCategoriesDistributions.reduce((sum, _, index) => {
        const productCategoryTotalQuantity = watch(`product_categories_distributions.${index}.total_quantity` as `product_categories_distributions.${number}.total_value`);

        return sum + (productCategoryTotalQuantity ? unFormatUnknownUnit(productCategoryTotalQuantity) : 0);
      }, 0);

      setValue('total_quantity' as 'total_value', formatDecimalUnit(productCategoriesTotalQuantitySum));
    } else {
      const productCategoriesTotalValueSum = productCategoriesDistributions.reduce((sum, _, index) => {
        const productCategoryTotalValue = watch(`product_categories_distributions.${index}.total_value`);

        return sum + (productCategoryTotalValue ? unFormatCurrency(productCategoryTotalValue) : 0);
      }, 0);

      setValue('total_value', formatCurrency(productCategoriesTotalValueSum));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Select
        placeholder="Selecione um serviço"
        isLoading={isAllowedCategoriesAndProductsFetching}
        options={allowedCategoriesOptions?.filter((productCategoryOption) => {
          return !productCategoriesDistributions?.some(({ product_category }) => product_category?.id === productCategoryOption.value);
        }) || []}
        value=""
        onChange={(newValue) => {
          if (isConsumptionControlledByQuantity) {
            appendProductCategoryDistribution({
              product_category: {
                id: (newValue as Option).value,
                display_name: (newValue as GovernmentAllocationAllowedCategoryAndProductData).display_name,
                products_count: ''
              },
              products_distributions: [],
              total_quantity: '',
            } as unknown as CreateGovernmentAllocationDistributionValidationSchema['product_categories_distributions'][number]);
          } else {
            appendProductCategoryDistribution({
              product_category: {
                id: (newValue as Option).value,
                display_name: (newValue as GovernmentAllocationAllowedCategoryAndProductData).display_name,
                products_count: ''
              },
              products_distributions: [],
              total_value: formatCurrency(0),
            });
          }

          setTotalValue();
        }}
      />

      {productCategoriesDistributions.map((productCategoryDistribution, index) => (
        <ProductCategoryDistributionFormItem
          key={`${uniqueId}-${productCategoryDistribution?.product_category?.id}`}
          productCategoryIndex={index}
          productCategoryDistribution={productCategoryDistribution as CompanyGovernmentAllocationProductCategoriesDistribution}
          onDelete={() => {
            removeProductCategoryDistribution(index);
            setTotalValue();
          }}
          onTotalValueChange={setTotalValue}
          distribution={distribution}
          isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
        />
      ))}
    </div>
  );
};

export default GovernmentAllocationDistributionProductCategoriesForm;
